import React, { useState, useEffect } from 'react';
import Icon from './icons';

interface ImageViewerProps {
  imageData: string;
  altText: string;
  placeholderIcon?: React.ReactNode;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ 
  imageData, 
  altText,
  placeholderIcon
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPanPosition, setStartPanPosition] = useState({ x: 0, y: 0 });
  const [touchDistance, setTouchDistance] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState(1);

  // Add zoom control functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3)); // Max zoom 3x
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5)); // Min zoom 0.5x
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  // Add mouse wheel handler for zooming
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Stop the event from propagating to parent elements
    e.stopPropagation();
    // Prevent the default scroll behavior
    e.preventDefault();
    
    // Determine zoom direction based on wheel delta
    if (e.deltaY < 0) {
      // Zoom in
      setZoomLevel(prev => Math.min(prev + 0.1, 3));
    } else {
      // Zoom out
      setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
    }

    // Return false to prevent default browser behavior
    return false;
  };

  // Add mouse event handlers for panning
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomLevel > 1) {
      setIsPanning(true);
      setStartPanPosition({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - startPanPosition.x,
        y: e.clientY - startPanPosition.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
  };

  // Add touch event handlers for pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2) {
      // Two finger touch - prepare for pinch zoom
      const distance = getTouchDistance(e.touches);
      setTouchDistance(distance);
      setInitialZoom(zoomLevel);
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single finger touch - prepare for panning
      setIsPanning(true);
      setStartPanPosition({
        x: e.touches[0].clientX - panPosition.x,
        y: e.touches[0].clientY - panPosition.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent default to avoid page scrolling
    
    if (e.touches.length === 2 && touchDistance !== null) {
      // Handle pinch zoom
      const newDistance = getTouchDistance(e.touches);
      const scale = newDistance / touchDistance;
      const newZoom = Math.max(0.5, Math.min(3, initialZoom * scale));
      setZoomLevel(newZoom);
    } else if (e.touches.length === 1 && isPanning && zoomLevel > 1) {
      // Handle panning with one finger
      setPanPosition({
        x: e.touches[0].clientX - startPanPosition.x,
        y: e.touches[0].clientY - startPanPosition.y
      });
    }
  };

  const handleTouchEnd = () => {
    setTouchDistance(null);
    setIsPanning(false);
  };

  // Helper function to calculate distance between two touch points
  const getTouchDistance = (touches: React.TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Add component lifecycle hook to add/remove wheel event listener
  useEffect(() => {
    // Function to prevent default wheel behavior
    const preventWheelDefault = (e: WheelEvent) => {
      const imageContainer = document.getElementById('image-zoom-container');
      if (imageContainer && imageContainer.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    // Add the event listener with passive: false to allow preventDefault
    window.addEventListener('wheel', preventWheelDefault, { passive: false });

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('wheel', preventWheelDefault);
    };
  }, []);

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-500">影像：</span>
        <div className="flex space-x-2">
          <button 
            onClick={handleZoomOut}
            className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
            title="缩小"
            disabled={zoomLevel <= 0.5}
          >
            <Icon iconName="OutlineRemoveCircleOutline" size={20} color="currentColor" />
          </button>
          <button 
            onClick={handleResetZoom}
            className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
            title="重置"
          >
            <Icon iconName="OutlineRefresh" size={20} color="currentColor" />
          </button>
          <button 
            onClick={handleZoomIn}
            className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
            title="放大"
            disabled={zoomLevel >= 3}
          >
            <Icon iconName="OutlineAddCircleOutline" size={20} color="currentColor" />
          </button>
          <span className="text-xs text-gray-500 flex items-center ml-2">
            {Math.round(zoomLevel * 100)}%
          </span>
        </div>
      </div>
      <div 
        id="image-zoom-container"
        className="bg-gray-100 rounded-md p-2 flex justify-center overflow-hidden"
        style={{ 
          height: '400px',
          cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none' // Prevent browser handling of touch events
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {imageData && imageData !== 'base64_data_removed_for_response_size' ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <img 
              src={`data:image/jpeg;base64,${imageData}`} 
              alt={altText} 
              className="object-contain transition-transform"
              style={{ 
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                maxHeight: '100%',
                maxWidth: '100%'
              }}
              draggable="false"
            />
          </div>
        ) : (
          <div className="h-64 w-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="mx-auto mb-2 h-24 w-24 flex items-center justify-center">
                {placeholderIcon || <Icon iconName="OutlineImage" size={64} color="#4a5568" />}
              </div>
              <p>图像数据不可用</p>
            </div>
          </div>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-1 text-center">
        {zoomLevel > 1 ? (
          <p>提示：按住鼠标拖动可平移图像</p>
        ) : (
          <p>提示：使用鼠标滚轮或双指缩放手势可放大/缩小图像</p>
        )}
      </div>
    </div>
  );
};

export default ImageViewer; 